import {
  IBlocksByTagsQuery,
  IBlockTagsQuery,
  OverrideSQuery,
} from '@rhizome/pgtypes';
import TippyHeadless from '@tippyjs/react/headless';
import { setWidgets } from 'apps/exegesis/pages/notes/dashboard';
import {
  colors,
  DarkenMedium,
  DarkenSlightly,
} from 'apps/exegesis/styles/themes';
import {
  useFetcher,
  usePaginatedQuery,
} from 'apps/exegesis/utils/DataFetchers';
import { useAuthor } from 'apps/exegesis/utils/useAuthor';
import { usePrivacy } from 'apps/exegesis/utils/usePrivacy';
import React, { useState } from 'react';
import styled from 'styled-components';
import ExcerptDisplay from '../ExcerptDisplay';
import { ErrorDisplay } from '../StatusUI/ErrorDisplay';
import { LoadingPulser } from '../StatusUI/LoadingPulser';
import { TagMenu } from '../TagDisplay';
import { BlocksByTagsWidgetProps, WidgetUtilityProps } from './WidgetWrapper';

export const BlocksByTagsWidget = (
  props: BlocksByTagsWidgetProps & WidgetUtilityProps
) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const privacy = usePrivacy();
  const author = useAuthor((state) => state.author);

  const {
    data: excerpts,
    status: excerptsStatus,
    fetchNextPage: fetchMoreExcerpts,
    isFetchingNextPage: fetchingMoreExcerpts,
  } = usePaginatedQuery<OverrideSQuery<IBlocksByTagsQuery>>(
    [
      'BlocksByTags',
      {
        tags: props.tags,
        public: privacy === 'public',
        creation_date: null,
        note_author: author || null,
        limit: true,
      },
    ],
    { paginateOn: 'creation_date', existingRef: containerRef }
  );

  const [showSettings, setShowSettings] = useState(false);
  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };
  const closeSettings = () => {
    setShowSettings(false);
  };

  const [searchField, setSearchField] = useState('');

  const handleSearchFieldChange = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (evt.target !== null) setSearchField(evt.target.value);
  };

  const { data: blockTags, status: tagStatus } = useFetcher<IBlockTagsQuery>(
    [
      'BlockTags',
      {
        tags: props.tags,
        public: privacy === 'public',
        author: author || null,
      },
    ],
    { keepPreviousData: true }
  );
  return (
    <Container>
      <Title>
        <TitleText>
          {props.tags.length > 0 ? 'blocks tagged ' : 'no tags selected. '}
        </TitleText>
        <TippyHeadless
          interactive={true}
          offset={[0, 0]}
          placement="bottom-start"
          visible={showSettings}
          arrow={false}
          onClickOutside={closeSettings}
          render={(attrs) => (
            <CustomTippy className="box" tabIndex={-1} {...attrs}>
              {blockTags ? (
                <TagMenu
                  tags={blockTags}
                  selectedTags={new Set(props.tags)}
                  setSelectedTags={(newSet: Set<string>) => {
                    const newWidgets = [...props.widgets];
                    (newWidgets[props.index] as BlocksByTagsWidgetProps).tags =
                      Array.from(newSet);
                    setWidgets(newWidgets);
                  }}
                  searchField={searchField}
                  setSearchField={setSearchField}
                  handleSearchFieldChange={handleSearchFieldChange}
                />
              ) : (
                <div>loading</div>
              )}
            </CustomTippy>
          )}
        >
          {props.tags.length > 0 ? (
            <TagList onClick={toggleSettings}>
              {props.tags.map((tag, i) => {
                if (i === props.tags.length - 1) {
                  return tag;
                }
                return `${tag}, `;
              })}
            </TagList>
          ) : (
            <TagList onClick={toggleSettings}>{'[select tags]'}</TagList>
          )}
        </TippyHeadless>
      </Title>
      {props.tags.length > 0 ? (
        <ScrollContainer ref={containerRef}>
          {excerptsStatus === 'loading' ? (
            <div style={{ height: '150px' }}>
              <LoadingPulser />
            </div>
          ) : null}
          {excerptsStatus === 'error' ? <ErrorDisplay /> : null}
          {excerpts?.pages.map((page, pageNumber) => {
            return page.map((excerpt, i) => {
              const uniqueId = excerpt.block_id + ' ' + i.toString();
              return (
                <ExcerptDisplay
                  key={uniqueId}
                  {...excerpt}
                  page={pageNumber}
                  isRoot={true}
                />
              );
            });
          })}
        </ScrollContainer>
      ) : null}
    </Container>
  );
};

const CustomTippy = styled.div`
  width: 250px;
  /* height: 250px; */
  -webkit-box-shadow: 1px 2px 14px -2px rgba(0, 0, 0, 0.15);
  box-shadow: 1px 2px 14px -2px rgba(0, 0, 0, 0.15);
  border: 1px solid ${colors.offBlack};
`;

export const Title = styled.h1`
  margin: 0px 0px 0.75rem 0px;
  font-size: 1rem;
`;

export const TitleText = styled.span`
  font-weight: normal;
`;

export const TagList = styled.span`
  padding: 0.1rem;
  background-color: ${DarkenSlightly};
  :hover {
    background-color: ${DarkenMedium};
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const ScrollContainer = styled.div`
  overflow-y: auto;
  // TODO this is because some elements don't break
  overflow-x: hidden;
`;
